// ===== domain/money.ts =====
// SUMBER KEBENARAN TUNGGAL untuk uang. Diport dari prototype v0.18 (lolos UAT).
// Aturan (TERKUNCI): pengakuan omzet/profit/komisi = pembayaran DIVERIFIKASI Finance
// (recognizedPaid). paidSoFar = kas aktual (hanya utk refund/serah). Biaya simpan =
// pendapatan lain-lain (BUKAN omzet). Semua fungsi MURNI + ada test (money.test.ts).

import type { Ticket, PaymentRound } from '@core/types';

const num = (x: unknown): number => (typeof x === 'number' && !isNaN(x) ? x : 0);

/** Daftar ronde pembayaran: riwayat (lama) + tiket berjalan. */
function rounds(t: Ticket): PaymentRound[] {
  return [...(t.riwayatServis ?? []), t];
}

/** Pembayaran final = ditandai lunas CS DAN diverifikasi Finance. */
/**
 * Cara bayar yang berarti unit dibawa pulang TANPA dibayar → jadi PIUTANG.
 * SATU opsi saja: "Piutang / Tempo". Dulu ada dua ("Tempo (Toko)" & "Piutang") padahal mekanismenya
 * identik — Apo menegur, benar. Nama ini memuat kedua kata sehingga kasir yang berpikir "pelanggan
 * ngutang" maupun "kasih tempo" sama-sama ketemu, dan data lama ber-"Tempo (Toko)" tetap terbaca.
 * Satu predikat bersama supaya penambahan opsi baru tak perlu menyisir ~19 tempat yang dulu
 * membandingkan string `=== 'Tempo (Toko)'` satu per satu (dan pasti ada yang terlewat).
 */
export const CARA_PIUTANG = ['Piutang / Tempo'] as const;
export const CARA_BAYAR = ['Tunai', 'Transfer', 'QRIS', ...CARA_PIUTANG] as const;
export const isPiutangCara = (cara?: string | null): boolean => /tempo|piutang/i.test(cara ?? '');
/** Jatuh tempo WAJIB untuk semua piutang — tanpa tanggal, tagihan tak bisa di-aging/ditagih.
 *  Tak memberatkan kasir karena tanggalnya terisi otomatis dari Pengaturan (jatuhTempoDefault). */
export const wajibJatuhTempo = isPiutangCara;
/**
 * Status LUNAS final. Hanya ditentukan cara bayar TERAKHIR (langkah Konfirmasi Pembayaran) —
 * bukan rencana di langkah sebelumnya. Dulu aturan ini dilipat DUA KALI (langkah 1 lalu langkah 2):
 * sekali jadi false ia tak pernah bisa true lagi, sehingga pelanggan yang batal tempo lalu membayar
 * tunai di kasir tetap tercatat BELUM BAYAR — uang di laci, tapi sistem menagihnya lagi.
 */
export const lunasFinal = (ditandaiLunas: boolean, caraFinal?: string | null): boolean =>
  ditandaiLunas && !isPiutangCara(caraFinal);

export function paymentFinal(r: PaymentRound | null | undefined): boolean {
  return !!(r && r.lunas && r.verified);
}

/** Kas yang DIAKUI (verified). DP tetap dihitung; sisa diakui saat verified. */
export function recognizedPaid(t: Ticket): number {
  return rounds(t).reduce(
    (a, r) => a + (paymentFinal(r) ? num(r.biaya) : num(r.dp)),
    0,
  );
}

/** Kas AKTUAL diterima (lunas, belum tentu verified) — hanya utk refund/serah. */
export function paidSoFar(t: Ticket): number {
  return rounds(t).reduce(
    (a, r) => a + (r.lunas ? num(r.biaya) : num(r.dp)),
    0,
  );
}

/** Refund yang sudah diverifikasi (mengurangi omzet) — semua ronde. */
export function refundTiket(t: Ticket): number {
  return rounds(t).reduce(
    (a, r) => a + (r.refund && r.refund.status === 'verified' ? num(r.refund.amount) : 0),
    0,
  );
}

/**
 * Jumlah TRANSAKSI utk CRM (aturan Apo 19 Jul 2026) — per SESI, bukan per tiket:
 *  · sesi yang benar-benar MEMBAYAR (kas diakui > 0) = +1
 *    → servis-ulang lewat garansi yang bayar lagi = 2 transaksi;
 *  · sesi klaim garansi GRATIS = +0 (transaksi tetap 1 — sesi asalnya);
 *  · sesi ber-REFUND terverifikasi = −1 (transaksinya dianggap batal).
 * Clamp ≥ 0 — refund tak pernah membuat pelanggan "minus transaksi".
 */
export function trxCount(t: Ticket): number {
  const n = rounds(t).reduce((a, r) => {
    const bayar = (paymentFinal(r) ? num(r.biaya) : num(r.dp)) > 0 ? 1 : 0;
    // Hanya refund yang MENGEMBALIKAN KAS (amount>0) yang mengurangi — refund Rp 0
    // (murni pembebasan hutang tempo, Batch 52) tak boleh menghapus transaksi sesi lain.
    const refund = r.refund && r.refund.status === 'verified' && num(r.refund.amount) > 0 ? 1 : 0;
    return a + bayar - refund;
  }, 0);
  return Math.max(0, n);
}

/** Total tagihan semua ronde (billed, belum tentu diterima). */
export function totalSemuaRonde(t: Ticket): number {
  return rounds(t).reduce((a, r) => a + num(r.biaya), 0);
}

/** Modal (HPP) semua ronde. */
export function modalTiket(t: Ticket): number {
  return rounds(t).reduce((a, r) => a + num(r.modal), 0);
}

/**
 * Dasar KOMISI teknisi. Beda dari `omzetTiket` di dua hal, keduanya soal klaim garansi:
 *
 * 1. **Sesi berjalan saja.** Saat unit balik klaim garansi, tanggal ambil tiket pindah ke bulan
 *    klaim — sementara `omzetTiket` menjumlah SEMUA sesi. Uang sesi lama ikut terhitung lagi di
 *    bulan klaim, jadi teknisi dibayar komisi dua kali untuk uang yang sama.
 * 2. **Boleh negatif** (aturan Apo 31 Agu): klaim garansi yang GAGAL memotong komisi teknisi dan
 *    mengurangi omzetnya di bulan klaim. Refund ke pelanggan harus terasa di bulan itu, bukan
 *    hilang tertelan clamp nol seperti pada `omzetTiket` (yang memang untuk laporan toko).
 *
 * Untuk tiket biasa (tanpa riwayat sesi) angkanya sama persis dengan `omzetTiket`.
 */
export function omzetKomisi(t: Ticket): number {
  return (paymentFinal(t) ? num(t.biaya) : num(t.dp)) - refundTiket(t);
}

/** Pasangan `omzetKomisi` untuk skema komisi berbasis profit — modal sesi berjalan saja. */
export function profitKomisi(t: Ticket): number {
  return omzetKomisi(t) - num(t.modal);
}

/** OMZET = kas diakui − refund (basis-kas, verified). Tak pernah negatif. */
export function omzetTiket(t: Ticket): number {
  return Math.max(0, recognizedPaid(t) - refundTiket(t));
}

/** PROFIT diakui = omzet − modal. */
export function profitTiket(t: Ticket): number {
  return omzetTiket(t) - modalTiket(t);
}

/** PIUTANG = sisa belum dibayar (+ biaya simpan belum bayar). 0 bila refund verified. */
export function piutangTiket(t: Ticket): number {
  if (t.refund && t.refund.status === 'verified') return 0;
  // Piutang HANYA muncul setelah unit DISERAHKAN (ada penjualan). Sesi lama (riwayatServis) sudah
  // pernah diserahkan; sesi berjalan hanya dihitung bila status tiket 'Sudah Diambil'. Unit yang
  // masih di "Bisa Diambil" belum ada transaksi → belum piutang (aturan Apo). HPP sparepart yang
  // sudah dibayar ke supplier tetap diakui via modalTiket (kerugian/HPP sementara), bukan piutang.
  const sesi: PaymentRound[] = [...(t.riwayatServis ?? []), ...(t.status === 'Sudah Diambil' ? [t] : [])];
  return sesi.reduce((a, r) => {
    if (r.lunas) return a;
    return a + Math.max(0, num(r.biaya) + num(r.biayaSimpan) - num(r.dp));
  }, 0);
}

/**
 * HPP normal saat unit DIAMBIL, setelah dikurangi part yang SUDAH diakui sebagai "HPP Sementara"
 * di periode sebelumnya (part terbayar ke supplier saat unit belum diambil). Cegah biaya part
 * terhitung DUA KALI lintas periode: Σ(hppNormalOf + sementara) = biaya part diakui SEKALI.
 */
export function hppNormalOf(hppRaw: number, priorPaid: number): number {
  return Math.max(0, num(hppRaw) - num(priorPaid));
}

/** Biaya simpan diakui (pendapatan lain-lain) — saat pembayaran ronde verified. */
export function biayaSimpanTiket(t: Ticket): number {
  return rounds(t).reduce(
    (a, r) => a + (paymentFinal(r) ? num(r.biayaSimpan) : 0),
    0,
  );
}

/** Tagihan tampil: pakai biaya final bila hasil sudah ada, else estimasi. */
export function billAmount(t: Ticket): number {
  return t.hasil ? num(t.biaya) : num(t.biaya) || num(t.estBiaya);
}

/** Tagihan operasional (Sudah Diambil): total semua sesi − refund. */
export function billedNet(t: Ticket): number {
  return Math.max(0, totalSemuaRonde(t) - refundTiket(t));
}
